import { z } from 'zod';

import { USER_ROLES, type UserRole } from '@/shared/model/enums';

/**
 * Immutable profile domain model.
 *
 * Phase 6 (D-014) — multi-role: `role` (the single enum from Phase 1) is retained
 * for back-compat but the source of truth is now the four boolean flags below.
 * A person can hold any combination: e.g. an appointed imam who also freelances
 * has `is_imam=true, is_appointed=true, is_freelance=true`. See `isProvider()`,
 * `isAppointed()`, `isFreelance()` helpers below.
 */
export interface Profile {
  readonly id: string;
  /** @deprecated Prefer the is_imam/is_muezzin/is_appointed/is_freelance flags. */
  readonly role: UserRole;
  readonly isImam: boolean;
  readonly isMuezzin: boolean;
  readonly isAppointed: boolean;
  readonly isFreelance: boolean;
  readonly fullName: string;
  readonly city: string | null;
  readonly bio: string | null;
  readonly yearsExperience: number | null;
  readonly qualifications: string | null;
  readonly onboardingComplete: boolean;
  readonly verified: boolean;
  readonly isAdmin: boolean;
  // Phase 2: freelancer service area (provider location + reachable radius).
  readonly serviceLat: number | null;
  readonly serviceLng: number | null;
  readonly serviceRadiusKm: number;
  // Phase 2 (TD-001): imam can deliver the Friday khutbah.
  readonly canKhutbah: boolean;
  // Phase 3: reputation (trigger-synced), certifier designation, location consent.
  readonly ratingAvg: number;
  readonly ratingCount: number;
  readonly reliabilityScore: number;
  readonly isCertifier: boolean;
  readonly locationConsentAt: string | null;
  readonly createdAt: string;
}

/** Row shape as returned by Supabase; validated before mapping. */
const profileRowSchema = z.object({
  id: z.string(),
  role: z.enum(USER_ROLES),
  is_imam: z.boolean(),
  is_muezzin: z.boolean(),
  is_appointed: z.boolean(),
  is_freelance: z.boolean(),
  full_name: z.string(),
  city: z.string().nullable(),
  bio: z.string().nullable(),
  years_experience: z.number().nullable(),
  qualifications: z.string().nullable(),
  onboarding_complete: z.boolean(),
  verified: z.boolean(),
  is_admin: z.boolean(),
  service_lat: z.number().nullable(),
  service_lng: z.number().nullable(),
  service_radius_km: z.number(),
  can_khutbah: z.boolean(),
  rating_avg: z.coerce.number(),
  rating_count: z.number(),
  reliability_score: z.coerce.number(),
  is_certifier: z.boolean(),
  location_consent_at: z.string().nullable(),
  created_at: z.string(),
});

export function mapProfile(row: unknown): Profile {
  const r = profileRowSchema.parse(row);
  return {
    id: r.id,
    role: r.role,
    isImam: r.is_imam,
    isMuezzin: r.is_muezzin,
    isAppointed: r.is_appointed,
    isFreelance: r.is_freelance,
    fullName: r.full_name,
    city: r.city,
    bio: r.bio,
    yearsExperience: r.years_experience,
    qualifications: r.qualifications,
    onboardingComplete: r.onboarding_complete,
    verified: r.verified,
    isAdmin: r.is_admin,
    serviceLat: r.service_lat,
    serviceLng: r.service_lng,
    serviceRadiusKm: r.service_radius_km,
    canKhutbah: r.can_khutbah,
    ratingAvg: r.rating_avg,
    ratingCount: r.rating_count,
    reliabilityScore: r.reliability_score,
    isCertifier: r.is_certifier,
    locationConsentAt: r.location_consent_at,
    createdAt: r.created_at,
  };
}

// ---------- Flag-based role predicates (Phase 6, D-014) ----------

/** True if the profile can perform prayers as an imam or muezzin. */
export function isProvider(p: Pick<Profile, 'isImam' | 'isMuezzin'>): boolean {
  return p.isImam || p.isMuezzin;
}

/** True if the profile has a permanent post (can request coverage for a masjid). */
export function isAppointed(p: Pick<Profile, 'isAppointed'>): boolean {
  return p.isAppointed;
}

/** True if the profile takes ad-hoc coverage jobs (appears in the freelancer feed). */
export function isFreelance(p: Pick<Profile, 'isFreelance'>): boolean {
  return p.isFreelance;
}

/** Which "modes" a user can operate in (drives tab layout + the role switcher).
 * A user can hold any combination. Freelance mode requires being a provider —
 * an is_freelance flag alone with neither is_imam nor is_muezzin is a
 * misconfiguration and produces no freelance mode. */
export function availableModes(
  p: Pick<Profile, 'isImam' | 'isMuezzin' | 'isAppointed' | 'isFreelance' | 'isAdmin'>,
): Array<'freelance' | 'appointed' | 'admin'> {
  const modes: Array<'freelance' | 'appointed' | 'admin'> = [];
  if (p.isFreelance && (p.isImam || p.isMuezzin)) modes.push('freelance');
  if (p.isAppointed) modes.push('appointed');
  if (p.isAdmin) modes.push('admin');
  return modes;
}

/** Resolves the effective mode from the user's chosen mode + their available modes.
 * If they've chosen a mode they still hold, honor it; otherwise default to the
 * first available. */
export function resolveActiveMode(
  p: Parameters<typeof availableModes>[0],
  chosen: 'freelance' | 'appointed' | 'admin' | null,
): 'freelance' | 'appointed' | 'admin' | null {
  const modes = availableModes(p);
  if (chosen && modes.includes(chosen)) return chosen;
  return modes[0] ?? null;
}

/** Values collected by the profile form / written on upsert. */
export interface ProfileWriteInput {
  readonly id: string;
  /** Legacy — kept for back-compat with the pre-Phase-6 row shape. Derived from
   * the flags below on upsert if not provided. */
  readonly role: UserRole;
  readonly isImam: boolean;
  readonly isMuezzin: boolean;
  readonly isAppointed: boolean;
  readonly isFreelance: boolean;
  readonly fullName: string;
  readonly city: string | null;
  readonly bio: string | null;
  readonly yearsExperience: number | null;
  readonly qualifications: string | null;
  readonly onboardingComplete: boolean;
  readonly serviceLat: number | null;
  readonly serviceLng: number | null;
  readonly serviceRadiusKm: number;
  readonly canKhutbah: boolean;
}

export function toProfileRow(input: ProfileWriteInput): Record<string, unknown> {
  return {
    id: input.id,
    role: input.role,
    is_imam: input.isImam,
    is_muezzin: input.isMuezzin,
    is_appointed: input.isAppointed,
    is_freelance: input.isFreelance,
    full_name: input.fullName,
    city: input.city,
    bio: input.bio,
    years_experience: input.yearsExperience,
    qualifications: input.qualifications,
    onboarding_complete: input.onboardingComplete,
    service_lat: input.serviceLat,
    service_lng: input.serviceLng,
    service_radius_km: input.serviceRadiusKm,
    can_khutbah: input.canKhutbah,
  };
}

/** Derives a legacy `role` value from the multi-role flags — for back-compat
 * with the still-NOT-NULL `role` column. Priority: appointed → imam → muezzin →
 * masjid_admin default. */
export function deriveLegacyRole(flags: {
  isImam: boolean;
  isMuezzin: boolean;
  isAppointed: boolean;
}): UserRole {
  if (flags.isImam) return 'imam';
  if (flags.isMuezzin) return 'muezzin';
  if (flags.isAppointed) return 'masjid_admin';
  return 'masjid_admin';
}
